import { Box, LargeText, BodyText } from '@epilot/base-elements'
import { Link } from 'react-router-dom'
import styled, { css } from 'styled-components'

import { MAX_BORDER_RADIUS, MEDIA_SIZES } from '../../../utils/StyleUtils'

interface EntityRowContainerProps {
  $shadow: boolean
  $disabled: boolean
}

// Attribute renderer UI elements

export const AttributesGroupContainer = styled.div<{
  $removeBorder: boolean
}>`
  background: white;
  border: 1px solid ${({ theme }) => theme.palette.grey[50]};
  border-radius: ${({ theme }) =>
    theme.shape.borderRadius > MAX_BORDER_RADIUS
      ? MAX_BORDER_RADIUS
      : theme.shape.borderRadius}px;

  @media ${MEDIA_SIZES.lower.mobileL} {
    border: ${({ $removeBorder }) =>
      $removeBorder
        ? 'none'
        : `1px solid ${({ theme }) => theme.palette.grey[50]}`};
  }
`

export const AttributeIconCircleContainer = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  height: 35px;
  width: 35px;
  border: 1px solid ${({ theme }) => theme.palette.grey[60]};
`

export const AttributeGroupContainer = styled.div`
  padding: 9px 16px;
  background: white;
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
  border: 1px solid ${({ theme }) => theme.palette.grey[40]};
  cursor: pointer;
  display: flex;
  align-items: center;

  ${AttributeIconCircleContainer} {
    transition: transform 0.08s ease-in-out;
  }

  @media ${MEDIA_SIZES.greater.tablet} {
    :hover {
      ${AttributeIconCircleContainer} {
        transform: translateX(6px);
      }
    }
  }
`

export const AttributeGroupsDialogContainer = styled.div`
  width: 100%;
  margin-top: 28px;
  margin-bottom: 20px;

  ${({ $moreThanOne }) =>
    $moreThanOne &&
    css`
      ${AttributeGroupContainer} {
        margin-top: -1px;
        margin-bottom: 2px;
      }

      ${AttributeGroupContainer}:first-child {
        border-top-left-radius: ${({ theme }) =>
          `${theme.shape.borderRadius}px`};
        border-top-right-radius: ${({ theme }) =>
          `${theme.shape.borderRadius}px`};
      }

      ${AttributeGroupContainer}:last-child {
        border-bottom-left-radius: ${({ theme }) =>
          `${theme.shape.borderRadius}px`};
        border-bottom-right-radius: ${({ theme }) =>
          `${theme.shape.borderRadius}px`};
      }
    `};
`

export const AttributeTitle = styled(LargeText)`
  font-weight: 600;
  flex: 1;
`

// Entity

export const EntityProductNameContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-left: 17px;
  flex: 1;
`

export const EntityIconContainer = styled.div`
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: white;
  border: 1px solid ${({ theme }) => theme.palette.grey[60]};
`

export const EntityAttributesContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-column-gap: 16px;
  grid-row-gap: 2px;

  @media ${MEDIA_SIZES.lower.tablet} {
    grid-template-columns: 1fr;
  }
`

export const EntitySubtitle = styled(BodyText)`
  color: ${({ theme }) => theme.palette.grey[200]};
  font-weight: 700;
  text-transform: uppercase;
  font-size: 10px;
  letter-spacing: 1.5px;
`

// Entity Row

export const RowContainer = styled.div<EntityRowContainerProps>`
  overflow: hidden;
  position: relative;
  display: flex;
  flex-direction: column;
  border: 1px solid ${({ theme }) => theme.palette.grey[50]};
  transition: box-shadow 0.2s;
  box-shadow: ${({ $shadow }) => ($shadow ? '0 0 0.5rem #c4cbd6' : 'none')};
  pointer-events: ${({ $disabled }) => ($disabled ? 'none' : 'all')};
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;

  &:not(:last-child) {
    margin-bottom: 12px;
  }

  @media ${MEDIA_SIZES.greater.tablet} {
    :hover {
      box-shadow: 0 0 0.5rem #c4cbd6;
    }
  }
`

// $uniformPadding is used for Entity skeleton
// By default there is more padding on the right cause EntityRow has absolute arrow
export const EntityCard = styled.div<{ $uniformPadding: boolean }>`
  display: flex;
  flex-direction: column;
  padding: 21px;
  background: white;

  ${({ $uniformPadding }) =>
    $uniformPadding &&
    css`
      padding: 21px 26px;

      @media ${MEDIA_SIZES.lower.tablet} {
        padding: 16px 12px;
      }
    `};
`

export const EmptyEntityRow = () => {
  return (
    <EntityRowContainer>
      <EntityCard>
        <Box alignItems="center" display="flex">
          <GreyStaticSkeleton
            $borderRadius="100%"
            $height="48px"
            $padding="20px"
            $width="48px"
          />
          <Box marginLeft="10px" width="100%">
            <EntityRowItemSkeleton $height="15px" $width="90%" />
            <EntityRowItemSkeleton $height="15px" $width="45%" />
          </Box>
        </Box>
      </EntityCard>
    </EntityRowContainer>
  )
}

export const RowContent = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;

  @media ${MEDIA_SIZES.lower.mobileL} {
    grid-template-columns: auto;
  }
`

export const RowIconContainer = styled.div`
  width: 48px;
  height: 48px;
  min-width: 48px;
  min-height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: ${({ theme }) => theme.palette.grey[30]};
  border: 1px solid ${({ theme }) => theme.palette.grey[60]};
  padding: 10px;
  margin-right: 16px;

  @media ${MEDIA_SIZES.lower.tablet} {
    margin-bottom: 8px;
  }
  @media ${MEDIA_SIZES.lower.mobileM} {
    margin-bottom: 8px;
    display: none;
  }
`

export const RowArrowIconContainer = styled(AttributeIconCircleContainer)`
  margin-left: 16px;
`

export const EntityRowContainer = styled(RowContainer)`
  ${RowArrowIconContainer} {
    transition: transform 0.08s ease-in-out;
  }

  @media ${MEDIA_SIZES.greater.tablet} {
    :hover {
      ${RowArrowIconContainer} {
        transform: translateX(6px);
      }
    }
  }
`

export const RowEntityName = styled(LargeText)`
  font-weight: 700;
  color: ${({ theme }) => theme.palette.grey[800]};

  @media ${MEDIA_SIZES.lower.tablet} {
    font-size: 14px !important;
  }
`

export const RowRouteLink = styled(Link)`
  text-decoration: none;
`

export const RowLabel = styled(BodyText)`
  color: ${({ theme }) => theme.palette.grey[200]};
`

export const RowGeneralInformationContainer = styled.div`
  display: flex;
  align-items: center;
  overflow: hidden;

  @media ${MEDIA_SIZES.lower.tablet} {
    width: 70%;
  }
`

export const RowFlexStartMobile = styled(Box)`
  display: flex;

  @media ${MEDIA_SIZES.greater.mobileL} {
    flex-direction: row;
    justify-content: flex-end;
  }

  @media ${MEDIA_SIZES.lower.tablet} {
    flex-direction: column;
  }
`

export const GreyStaticSkeleton = styled.div`
  height: ${({ $height = '40px' }) => $height};
  width: ${({ $width = '100%' }) => $width};
  background: ${({ theme }) => theme.palette.grey[50]};
  border-radius: ${({ theme, $borderRadius }) =>
    $borderRadius ? $borderRadius : theme.shape.borderRadius + 'px'};
  padding: ${({ $padding = '0' }) => $padding};

  @keyframes loadingPlaceholder {
    0% {
      background-position: -475px 0;
    }
    100% {
      background-position: 475px 0;
    }
  }

  animation-duration: 1.5s;
  animation-fill-mode: forwards;
  animation-iteration-count: infinite;
  animation-name: loadingPlaceholder;
  animation-timing-function: linear;
  background: ${({ theme }) =>
    `linear-gradient(to right, ${theme.palette.grey[60]} 8%, ${theme.palette.grey[30]} 18%, ${theme.palette.grey[60]} 33%)`};
  background: ${({ theme }) =>
    `linear-gradient(to right, ${theme.palette.grey[60]} 8%, ${theme.palette.grey[30]} 18%, ${theme.palette.grey[60]} 33%)`};
  background-size: 950px 100px;
  position: relative;
  overflow: hidden;
`

export const EntityRowItemSkeleton = styled(GreyStaticSkeleton)`
  &:not(:last-child) {
    margin-bottom: 8px;
  }
`

export const HideEntityRowMobile = styled(Box)`
  display: flex;
  align-items: center;

  @media ${MEDIA_SIZES.lower.tablet} {
    display: none;
  }
`

export const HideMobile = styled(Box)`
  @media ${MEDIA_SIZES.lower.laptopS} {
    display: none;
  }
`

export const HideDesktop = styled(Box)`
  @media ${MEDIA_SIZES.greater.laptopS} {
    display: none;
  }
`

export const EntityListSkeleton = () => {
  return (
    <>
      <EmptyEntityRow />
      <EmptyEntityRow />
      <EmptyEntityRow />
      <EmptyEntityRow />
    </>
  )
}

export const EmptyEntityDetailsContent = () => {
  return (
    <Box marginTop="30px">
      <Box alignItems="center" display="flex">
        <GreyStaticSkeleton
          $borderRadius="100%"
          $height="48px"
          $padding="20px"
          $width="48px"
        />
        <Box marginLeft="10px" width="100%">
          <EntityRowItemSkeleton $height="15px" $width="45%" />
          <EntityRowItemSkeleton $height="15px" $width="20%" />
        </Box>
      </Box>
      <Box marginTop="40px">
        <EntityRowItemSkeleton $height="15px" $width="30%" />
        <EntityRowItemSkeleton $height="15px" $width="15%" />
      </Box>
      <Box marginTop="40px">
        <EntityRowItemSkeleton $height="40px" />
        <EntityRowItemSkeleton $height="40px" />
        <EntityRowItemSkeleton $height="40px" />
        <EntityRowItemSkeleton $height="40px" />
      </Box>
    </Box>
  )
}
